import {
  provideHttpClient,
  withInterceptorsFromDi,
  withXhr,
} from '@angular/common/http';
import {
  ApplicationConfig,
  importProvidersFrom,
  inject,
  provideAppInitializer,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { RouterModule } from '@angular/router';
import {
  BASE_PATH,
  CoreService,
} from '@nx-vigil/central/web/generated/api-client';
import { getRoutes } from '@nx-vigil/central/web/shell/feature';
import {
  AuthGuardAutoLogin,
  AuthService,
  Identity,
  setupAuth,
} from '@nx-vigil/core/web/auth/feature';
import {
  CORE_ENVIRONMENT_DEFAULTS,
  CORE_SERVICE,
  EnvironmentStore,
} from '@nx-vigil/core/web/environment/data-access';
import { provideProductPortal } from '@nx-vigil/core/web/shell/feature';
import {
  CORE_ORG_PRODUCT_LOGO,
  CORE_PROFILE_USER,
} from '@nx-vigil/core/web/shell/ui';
import { themePreset } from '@nx-vigil/core/web/theme/ui';
import {
  provideTanStackQuery,
  QueryClient,
} from '@tanstack/angular-query-experimental';
import { withDevtools } from '@tanstack/angular-query-experimental/devtools';
import { OAuthModule } from 'angular-oauth2-oidc';
import {
  LoggerModule,
  NGXLogger,
  TOKEN_LOGGER_SERVER_SERVICE,
} from 'ngx-logger';
import { provideOptimus } from '@openng/optimus-ui/config';
import { environment } from '../environment';
import { setupLogging } from '../initializers/logging';
import { setupTracing } from '../initializers/tracing';
import { OTELAttributesService } from '../open-telemetry/otel-attributes.service';
import { OTELLoggerWriterService } from '../open-telemetry/otel-logger-writer.service';
import { provideFeatureRoutes } from './feature-routes';

export function createConfig(
  identity: Identity,
  features: { [key: string]: boolean },
): ApplicationConfig {
  return {
    providers: [
      { provide: BASE_PATH, useValue: `${location.origin}/api` },
      { provide: CORE_SERVICE, useClass: CoreService },
      {
        provide: CORE_PROFILE_USER,
        useFactory: () => {
          const authService = inject(AuthService);
          return toSignal(authService.user$);
        },
      },
      provideProductPortal(),
      { provide: CORE_ENVIRONMENT_DEFAULTS, useValue: environment },
      { provide: CORE_ORG_PRODUCT_LOGO, useValue: environment.productLogo },
      provideHttpClient(withXhr(), withInterceptorsFromDi()),
      importProvidersFrom(
        BrowserAnimationsModule,
        RouterModule.forRoot([], {
          bindToComponentInputs: true,
        }),
        LoggerModule.forRoot(null, {
          serverProvider: {
            provide: TOKEN_LOGGER_SERVER_SERVICE,
            useClass: OTELLoggerWriterService,
          },
        }),
        OAuthModule.forRoot({
          resourceServer: {
            allowedUrls: [`${location.origin}/api`],
            sendAccessToken: true,
          },
        }),
      ),
      AuthGuardAutoLogin,
      provideAppInitializer(() => {
        const initializerFn = setupAuth(
          { ...identity, scopes: ['organization'] },
          inject(AuthService),
        );
        return initializerFn();
      }),
      EnvironmentStore,
      OTELAttributesService,
      provideTanStackQuery(
        new QueryClient(),
        withDevtools(() => ({
          loadDevtools: features['tanstack-devtools'] ?? 'auto',
        })),
      ),
      provideAppInitializer(() => {
        const initializerFn = setupLogging(
          inject(EnvironmentStore),
          inject(OTELAttributesService),
          inject(NGXLogger),
        );
        return initializerFn();
      }),
      provideAppInitializer(() => {
        const initializerFn = setupTracing(
          inject(EnvironmentStore),
          inject(OTELAttributesService),
        );
        return initializerFn();
      }),
      provideAnimationsAsync(),
      provideOptimus({
        theme: {
          preset: themePreset,
          options: { darkModeSelector: '.dark' },
        },
      }),
      provideFeatureRoutes(() => {
        const environmentStore = inject(EnvironmentStore);
        const authService = inject(AuthService);
        return {
          features$: environmentStore.features$,
          getRoutes,
          refreshFeatures: () => environmentStore.refresh(),
          isAuthenticated$: authService.isAuthenticated$,
        };
      }),
    ],
  };
}
